import React, { useEffect, useMemo, useState } from 'react';
import EmailPreview from '../EmailPreview';
import TestEmailButton from '../TestEmailButton';
import ResetTemplateConfirmDialog from '../confirm/ResetTemplateConfirmDialog';
import ActionButton from '../../../../../shared/components/action-button/ActionButton';
import GuidedEmailTemplateForm from '../guided/GuidedEmailTemplateForm';
import {
  buildGuidedTemplateBody,
  createDefaultGuidedTemplate,
  createGuidedTemplateFromBody
} from '../../utils/guided/templateBuilder';
import './SimpleEmailTemplateEditor.css';

const buildBodyFromBlocks = (blocks = []) => {
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return '';
  }

  return blocks
    .map((block) => block.content)
    .filter(Boolean)
    .map((content) => `<p>${content.split('\n').join('<br />')}</p>`)
    .join('\n');
};

const FIELD_EXPLANATIONS = {
  customerName: 'Replaced with the client name.',
  service: 'Replaced with the booked service.',
  date: 'Replaced with the appointment date.',
  time: 'Replaced with the appointment time.',
  oldDate: 'Replaced with the original appointment date.',
  oldTime: 'Replaced with the original appointment time.',
  newDate: 'Replaced with the updated appointment date.',
  newTime: 'Replaced with the updated appointment time.',
  depositAmount: 'Replaced with the deposit amount paid.',
  remainingBalance: 'Replaced with the amount still due.',
  totalAmount: 'Replaced with the full appointment total.',
  balanceAmount: 'Replaced with the balance payment amount.',
  portalUrl: 'Replaced with your client portal link.'
};

const normalizeTemplate = (template) => ({
  subject: template?.subject || '',
  body: template?.body || buildBodyFromBlocks(template?.blocks),
  enabled: template?.enabled ?? true
});

const appendToken = (value, field) => {
  const token = `{{${field}}}`;
  if (!value) {
    return token;
  }

  return value.includes(token) ? value : `${value.trimEnd()} ${token}`;
};

const EDITOR_MODES = {
  guided: 'guided',
  advanced: 'advanced',
  preview: 'preview'
};

const HIDDEN_MERGE_FIELDS = ['portalUrl'];

const SimpleEmailTemplateEditor = ({
  title,
  description,
  template,
  defaultTemplate,
  mergeFields,
  onApply,
  onTestEmail,
  saving,
  companyData
}) => {
  const visibleMergeFields = useMemo(
    () => mergeFields.filter((field) => !HIDDEN_MERGE_FIELDS.includes(field)),
    [mergeFields]
  );
  const [draft, setDraft] = useState(() => normalizeTemplate(template));
  const [guidedDraft, setGuidedDraft] = useState(() =>
    createGuidedTemplateFromBody(normalizeTemplate(template).body, mergeFields)
  );
  const [editorMode, setEditorMode] = useState(EDITOR_MODES.guided);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  useEffect(() => {
    const normalized = normalizeTemplate(template);
    setDraft(normalized);
    setGuidedDraft(createGuidedTemplateFromBody(normalized.body, mergeFields));
  }, [template, mergeFields]);

  useEffect(() => {
    if (!draft.body.includes('<!-- guided-template')) {
      return;
    }

    setDraft((current) => ({
      ...current,
      body: buildGuidedTemplateBody(guidedDraft, mergeFields, companyData)
    }));
  }, [companyData, draft.body, guidedDraft, mergeFields]);

  const normalizedTemplate = useMemo(() => normalizeTemplate(template), [template]);
  const normalizedDefaultTemplate = useMemo(
    () => normalizeTemplate(defaultTemplate || template),
    [defaultTemplate, template]
  );
  const recommendedGuidedTemplate = useMemo(
    () => createDefaultGuidedTemplate(mergeFields),
    [mergeFields]
  );
  const hasChanges = draft.subject !== normalizedTemplate.subject || draft.body !== normalizedTemplate.body;
  const canResetTemplate =
    draft.subject !== normalizedDefaultTemplate.subject || draft.body !== normalizedDefaultTemplate.body;
  const isPreviewMode = editorMode === EDITOR_MODES.preview;

  const updateGuidedDraft = (nextGuidedDraft) => {
    setGuidedDraft(nextGuidedDraft);
    setDraft((current) => ({
      ...current,
      body: buildGuidedTemplateBody(nextGuidedDraft, mergeFields, companyData)
    }));
  };

  const handleModeChange = (nextMode) => {
    if (nextMode === EDITOR_MODES.guided) {
      setGuidedDraft(createGuidedTemplateFromBody(draft.body, mergeFields));
    }

    setEditorMode(nextMode);
  };

  const handleResetLayout = () => {
    if (!canResetTemplate) {
      return;
    }
    setResetDialogOpen(true);
  };

  const handleConfirmResetLayout = () => {
    const nextDraft = {
      ...normalizedDefaultTemplate,
      enabled: true
    };

    setGuidedDraft(recommendedGuidedTemplate);
    setDraft(nextDraft);
    setEditorMode(EDITOR_MODES.guided);
    setResetDialogOpen(false);
    onApply({ ...defaultTemplate, ...template, ...nextDraft });
  };

  return (
    <div className="simple-email-editor">
      <ResetTemplateConfirmDialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        onConfirm={handleConfirmResetLayout}
        title={title}
        saving={saving}
      />

      <div className="simple-email-editor__header">
        <div>
          <h3 className="simple-email-editor__title">{title}</h3>
          <p className="simple-email-editor__description">{description}</p>
        </div>

        <div className="simple-email-editor__actions app-action-toolbar">
          <TestEmailButton onSendTest={onTestEmail} disabled={saving} />
          <ActionButton
            disabled={!canResetTemplate || saving}
            onClick={handleResetLayout}
          >
            Reset default template
          </ActionButton>
          <ActionButton
            variant="primary"
            disabled={!hasChanges || saving}
            onClick={() => onApply({ ...template, ...draft, enabled: true })}
          >
            Apply changes
          </ActionButton>
        </div>
      </div>

      {!isPreviewMode ? (
        <>
          <div className="simple-email-editor__tutorial">
            <h4 className="simple-email-editor__tutorial-title">How these fields work</h4>
            <p className="simple-email-editor__tutorial-copy">
              Do not replace these yourself. Leave tokens like <code>{'{{customerName}}'}</code> in the subject or body exactly as shown, and the system will swap them for the real appointment details automatically before the email sends.
            </p>
            <div className="simple-email-editor__tutorial-list">
              {visibleMergeFields.map((field) => (
                <div key={field} className="simple-email-editor__tutorial-item">
                  <code>{`{{${field}}}`}</code>
                  <span>{FIELD_EXPLANATIONS[field] || 'This placeholder is replaced automatically with the matching appointment value.'}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      <div className="simple-email-editor__mode-switch" role="tablist" aria-label="Template editor modes">
        <button
          type="button"
          className={`simple-email-editor__mode-button ${editorMode === EDITOR_MODES.guided ? 'active' : ''}`}
          onClick={() => handleModeChange(EDITOR_MODES.guided)}
        >
          Guided builder
        </button>
        <button
          type="button"
          className={`simple-email-editor__mode-button ${editorMode === EDITOR_MODES.advanced ? 'active' : ''}`}
          onClick={() => handleModeChange(EDITOR_MODES.advanced)}
        >
          Advanced HTML
        </button>
        <button
          type="button"
          className={`simple-email-editor__mode-button ${editorMode === EDITOR_MODES.preview ? 'active' : ''}`}
          onClick={() => handleModeChange(EDITOR_MODES.preview)}
        >
          Preview
        </button>
      </div>

      <div className="simple-email-editor__fields">
        {!isPreviewMode ? (
          <label className="simple-email-editor__field">
            <span className="simple-email-editor__label">Subject</span>
            <input
              className="simple-email-editor__input"
              type="text"
              value={draft.subject}
              onChange={(event) => setDraft((current) => ({ ...current, subject: event.target.value }))}
            />
            <div className="simple-email-editor__insert-row">
              {visibleMergeFields.map((field) => (
                <button
                  key={`subject-${field}`}
                  type="button"
                  className="simple-email-editor__insert-token"
                  onClick={() =>
                    setDraft((current) => ({
                      ...current,
                      subject: appendToken(current.subject, field)
                    }))
                  }
                >
                  {`Add {{${field}}}`}
                </button>
              ))}
            </div>
            <span className="simple-email-editor__helper">Click a field to add it into the subject line.</span>
          </label>
        ) : null}

        {isPreviewMode ? (
          <EmailPreview
            subject={draft.subject}
            body={draft.body}
            companyLogoUrl={companyData?.logoUrl}
            companyName={companyData?.name}
            showSubject={false}
          />
        ) : null}

        {editorMode === EDITOR_MODES.guided ? (
          <GuidedEmailTemplateForm
            value={guidedDraft}
            mergeFields={mergeFields}
            visibleMergeFields={visibleMergeFields}
            onChange={updateGuidedDraft}
          />
        ) : null}

        {editorMode === EDITOR_MODES.advanced ? (
          <label className="simple-email-editor__field">
            <span className="simple-email-editor__label">Email body HTML</span>
            <textarea
              className="simple-email-editor__textarea"
              rows={12}
              value={draft.body}
              onChange={(event) => setDraft((current) => ({ ...current, body: event.target.value }))}
            />
            <span className="simple-email-editor__helper">
              Use HTML here if you want complete control over the email layout.
            </span>
          </label>
        ) : null}
      </div>
    </div>
  );
};

export default SimpleEmailTemplateEditor;
